import { Suspense, lazy, useEffect } from 'react';

import { Helmet } from 'react-helmet';

import { useLogger } from '@ezbobdev/ui-commons';
import { MainContext, UIFramework, UI_FW_MODES, checkIsIframe, t } from '@ezbobdev/ui-framework';
import { Tools } from '@ezbobdev/ui-sdk';
import {
  ErrorBoundary,
  Flow,
  LicenseInfo,
  MaterialAppBar,
  MaterialIFrameDialog,
  MaterialTheme,
  Modal,
} from '@ezbobdev/ui-widgets-ezbob';

import './App.css';
import config from './config/config';
import { helpType } from './utils/constants';
import { EzxBody, IframeCloseButton, LogoutButton } from './widgets';

const ErrorPage = lazy(() => import('./pages/ErrorPage/ErrorPage'));
const ExitFlowLazy = lazy(() => import('./pages/ExitFlow/ExitFlow'));

// Modal clones this child and injects its modalView.props onto it, so the
// Suspense boundary has to live inside a forwarding wrapper — wrapping the
// <ExitFlow> element directly would put the props on <Suspense> instead.
// Without the boundary the first X tap suspends on the lazy chunk during a
// synchronous click update, React throws #426, and the root ErrorBoundary
// swaps the whole app for ErrorPage — the blank white page in GRN-8372.
const ExitFlow = (props) => (
  <Suspense fallback={null}>
    <ExitFlowLazy {...props} />
  </Suspense>
);

const isCookieAuth = true;
const uiDomain = process.env.REACT_APP_TENANT_DOMAIN || window.location.host;
const pathSegments = window.location.pathname.split('/').filter(Boolean);
const productPath = pathSegments[0] || 'customer';

// Try to get host from API_HOSTS map first, then fallback to API_HOST
let host;
try {
  const apiHosts = JSON.parse(window.API_HOSTS || '{}');
  host = apiHosts[uiDomain];
} catch (error) {
  console.warn('Failed to parse API_HOSTS:', error);
}

// Fallback to single API_HOST if not found in map
if (!host) {
  host = window.API_HOST;
}

if (!host) {
  console.log('Application environment is not set properly.');
}

const authConfig = {
  withCredentials: isCookieAuth,
  grantType: 'client_proxy',
  refererDomain: uiDomain,
  productPath: productPath !== 'customer' ? productPath : undefined,
};

Tools.setAuthConfig(authConfig);
Tools.setOption('host', host);
Tools.setOption('protocol', window.API_PROTOCOL || window.location.protocol.replace(':', ''));

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUIX_KEY);

function App() {
  const logger = useLogger();

  useEffect(() => {
    const isProd = process.env.NODE_ENV === 'production';

    logger.init(isProd ? 'warn' : 'debug');
  }, [logger]);

  // TODO: Set REACT_APP_MUIX_KEY to all devs and then uncomment the check
  // if (!process.env.REACT_APP_MUIX_KEY) return;

  return (
    <ErrorBoundary
      fallback={
        <Suspense>
          <ErrorPage />
        </Suspense>
      }
      didCatch={logger.error}
    >
      <UIFramework config={config} mode={UI_FW_MODES.express}>
        <MainContext.Consumer>
          {({ actions }) => {
            const { getUserConfigProp } = actions.settings;

            const { logo, channelLogo } = getUserConfigProp('assets');
            const theme = getUserConfigProp('materialTheme');
            const allowSearchIndexing = getUserConfigProp('system.allowSearchIndexing') ?? true;
            const showSkipLink = getUserConfigProp('custom.showSkipLink');
            const isRouteMounted = !!actions.view.getView('routeSettings.name');
            const secondaryLogo = channelLogo ? (
              <img style={{ maxHeight: channelLogo.height }} src={channelLogo.src} alt={channelLogo.alt} />
            ) : undefined;

            return (
              <>
                {showSkipLink && (
                  <a className="skipLink" href="#content">
                    {t('globals.skipLink')}
                  </a>
                )}
                {!allowSearchIndexing && (
                  <Helmet>
                    <meta name="robots" content="noindex" />
                  </Helmet>
                )}
                <MaterialTheme theme={theme}>
                  <Flow>
                    {isRouteMounted && (
                      <MaterialAppBar
                        name="materialAppBar"
                        muiAppBar={{ elevation: 0 }}
                        logo={
                          <img style={{ maxHeight: logo.height, width: logo.width }} src={logo.src} alt={logo.alt} />
                        }
                        secondaryLogo={secondaryLogo}
                        logoutButton={<LogoutButton />}
                        helpType={helpType}
                        actions={true}
                        containerMaxWidth={'xl'}
                        overrideConfig={(config) => {
                          if (config?.iframeBasedVisibility) {
                            config.hidden = checkIsIframe();
                          }
                          return config;
                        }}
                      />
                    )}

                    <Modal modalName="ExitFlow">
                      <ExitFlow name="exitFlow" />
                    </Modal>

                    <IframeCloseButton />
                    <EzxBody />

                    <Modal modalName="GlobalIframe">
                      <MaterialIFrameDialog open={false} />
                    </Modal>
                  </Flow>
                </MaterialTheme>
              </>
            );
          }}
        </MainContext.Consumer>
      </UIFramework>
    </ErrorBoundary>
  );
}

export default App;
